import { IBaseConfiguration } from "../interfaces/configuration/IBaseConfiguration";
import { IExternalConfiguration } from "../interfaces/configuration/IExternalConfiguration";
import Axios from "axios";
import { fetchGlobalConfigurationUrl, userDisplaySettingsUrl, userFiltersUrl, translationUrl, removeTrailingSlash, fetchGlobalConfigurations } from "../helpers/urlHelper";
import { IFilter, IFilterGroup } from "../interfaces/filters/IFilter";
import { DisplaySettings } from "src/rematch/interfaces/states/IViewSettingsState";

import devconfig from "../configuration/devconfig.json";
import displaySettingsDefaults from "../../rematch/defaults/DisplaySettingDefaults";
import { validateConfiguration } from "../configuration/validation";
import i18n from "../../i18n";
import { logError, LOGERROR_VALIDATION_FAILED } from "./LoggingService";

// GET

export const getConfigFromNVSAsync = async (baseConfiguration: IBaseConfiguration): Promise<IExternalConfiguration> => {
  if (baseConfiguration.devModeOn) {
    return (devconfig as unknown) as IExternalConfiguration;
  } else {
    const url = fetchGlobalConfigurationUrl(baseConfiguration.envName);
    const response = await Axios.get(url, { baseURL: baseConfiguration.nvsApiUrl });
    const errors = validateConfiguration(response.data);
    if (errors?.length > 0) {
      let errorMsg = "Validation errors:";
      for(const error of errors) {
        errorMsg += `\n${error.instancePath} ${error.message}`
      }
      logError(LOGERROR_VALIDATION_FAILED, errorMsg);
      throw new Error(i18n.t("error.invalidconfiguration"));
    }
    const config: IExternalConfiguration = response.data as IExternalConfiguration;
    if(config.mfilesApiBaseUrl) {
      config.mfilesApiBaseUrl = removeTrailingSlash(config.mfilesApiBaseUrl);
    }
    return config;
  }
};


export const getConfigsFromNVSAsync = async (baseConfiguration: IBaseConfiguration): Promise<{ modified: boolean, names: string[] }> => {

  const url = fetchGlobalConfigurations();
  const response = await Axios.get(url, { baseURL: baseConfiguration.nvsApiUrl });

  const config: { modified: boolean, names: string[] } = response.data;

  return config;
};

export const getUserDisplaySettingsFromNVSAsync = async (
  baseConfiguration: IBaseConfiguration,
  userId: number
): Promise<DisplaySettings> => {
  if (baseConfiguration.devModeOn) {
    return displaySettingsDefaults;
  } else {
    const url = userDisplaySettingsUrl(baseConfiguration.envName, userId);
    const response = await Axios.get(url, { baseURL: baseConfiguration.nvsApiUrl });
    return response.data as DisplaySettings;
  }
};

// TODO refine this
function isIFilterGroup(data: IFilter | IFilterGroup): data is IFilterGroup {
  return (data as IFilterGroup).conditionType !== undefined;
}

export const getUserFiltersFromNVSAsync = async (baseConfiguration: IBaseConfiguration, userId: number): Promise<IFilterGroup[][]> => {
  if (baseConfiguration.devModeOn) {
    return [];
  } else {
    try {
      const url = userFiltersUrl(baseConfiguration.envName, userId);
      const response = await Axios.get(url, { baseURL: baseConfiguration.nvsApiUrl });

      const viewFilters = (response.data as IFilterGroup[][]) || [];
    
      for(const filters of viewFilters) {
        if(filters) {
          for(const filter of filters) {
            filter.ownerId = userId
          }
        }
      }

      return viewFilters;

    } catch {
      // Might result in 404 if user has no filters saved previously
      return [];
    }
  }
};

// PUT

export const setConfigToNVSAsync = async (
  baseConfiguration: IBaseConfiguration,
  config: IExternalConfiguration
): Promise<void> => {
  if (!baseConfiguration.devModeOn) {
    const url = fetchGlobalConfigurationUrl(baseConfiguration.envName);
    await Axios.put(url, config, { baseURL: baseConfiguration.nvsApiUrl });
    //refetch for cache strategy
    const _ignorePromise = getConfigFromNVSAsync(baseConfiguration);
  }
}

export const setUserdisplaySettingsToNVSAsync = async (
  baseConfiguration: IBaseConfiguration,
  userId: number,
  displaySettings: DisplaySettings
): Promise<void> => {
  if (!baseConfiguration.devModeOn) {
    const url = userDisplaySettingsUrl(baseConfiguration.envName, userId);
    await Axios.put(url, displaySettings, { baseURL: baseConfiguration.nvsApiUrl });
    //refetch for cache strategy
    const _ignorePromise = getUserDisplaySettingsFromNVSAsync(baseConfiguration, userId);
  }
};

export const setUserFiltersToNVSAsync = async (baseConfiguration: IBaseConfiguration, userId: number, filterGroups: IFilterGroup[][]): Promise<void> => {
  if (!baseConfiguration.devModeOn) {
    const url = userFiltersUrl(baseConfiguration.envName, userId);
    await Axios.put(url, filterGroups, { baseURL: baseConfiguration.nvsApiUrl });
    //refetch for cache strategy
    const _ignorePromise = getUserFiltersFromNVSAsync(baseConfiguration, userId);
  }
};

export const setTranslationToNVSAsync = async (baseConfiguration: IBaseConfiguration, language: string, data: string): Promise<void> => {
  if (!baseConfiguration.devModeOn) {
    const url = translationUrl(language);
    const headers = { "Content-Type": "application/json; charset=utf-8" }; 
    await Axios.put(url, data, { headers: headers, baseURL: baseConfiguration.nvsApiUrl });
  }
};
